import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { FrontierServiceQueries, useQuery } from '@raystack/frontier/hooks';
import { useFrontier, useTokens } from '@raystack/frontier/react';

import { ReactComponent as SparklesIcon } from '@src/assets/StateIcons/sparkles.svg';
import {
  announcementMap,
  resetAnnouncement,
  setAnnouncement
} from '@src/redux/_reducers/appReducer';
import { setIAMDialogState } from '@src/redux/_reducers/authReducer';
import {
  OrganizationStateMap,
  SubscriptionStateMap
} from '@src/utils/constants/APP';

export function useBilling() {
  const dispatch = useDispatch();

  const {
    activeSubscription,
    activeOrganization,
    billingDetails,
    billingAccount
  } = useFrontier();
  const { tokenBalance } = useTokens();

  // Check permission for activeOrganization at top level
  const { data: isAllowed = false } = useQuery(
    FrontierServiceQueries.checkResourcePermission,
    {
      resource: `app/organization:${activeOrganization?.id}`,
      permission: 'update'
    },
    {
      enabled: !!activeOrganization?.id,
      select: (response) => Boolean(response.status)
    }
  );

  const checkCreditLimit = useCallback(() => {
    const CREDIT_LIMIT_ANNOUNCEMENT_ID = 'credit-limit-reached';

    if (
      billingAccount?.orgId &&
      billingDetails?.creditMin &&
      tokenBalance &&
      billingDetails.creditMin < BigInt(0)
    ) {
      if (BigInt(tokenBalance) <= billingDetails.creditMin) {
        const action = isAllowed
          ? () =>
              dispatch(setIAMDialogState({ isOpen: true, route: '/billing' }))
          : null;
        dispatch(
          setAnnouncement({
            id: CREDIT_LIMIT_ANNOUNCEMENT_ID,
            type: announcementMap.error,
            message:
              'Your limit for token usage is reached. Please recharge the tokens or reach out to our support team for further resolution.',
            action: action,
            actionLabel: 'Pay now →',
            leadingIcon: <SparklesIcon />,
            priority: 1
          })
        );
      } else {
        dispatch(resetAnnouncement(CREDIT_LIMIT_ANNOUNCEMENT_ID));
      }
    }
  }, [
    billingAccount?.orgId,
    billingDetails?.creditMin,
    isAllowed,
    tokenBalance,
    dispatch
  ]);

  const checkSubscriptionState = useCallback(() => {
    const SUBSCRIPTION_ANNOUNCEMENT_ID = 'subscription-past-due';

    if (
      activeOrganization?.id &&
      activeOrganization?.state === OrganizationStateMap.ENABLED
    ) {
      if (activeSubscription?.state === SubscriptionStateMap.PAST_DUE) {
        const action = isAllowed
          ? () =>
              dispatch(setIAMDialogState({ isOpen: true, route: '/billing' }))
          : null;
        dispatch(
          setAnnouncement({
            id: SUBSCRIPTION_ANNOUNCEMENT_ID,
            type: announcementMap.error,
            message: 'Your payment is due. Please try again.',
            action: action,
            actionLabel: 'Retry',
            leadingIcon: <ExclamationTriangleIcon />,
            priority: 1
          })
        );
      } else {
        dispatch(resetAnnouncement(SUBSCRIPTION_ANNOUNCEMENT_ID));
      }
    }
  }, [
    activeOrganization?.id,
    activeOrganization?.state,
    dispatch,
    isAllowed,
    activeSubscription?.state
  ]);

  useEffect(() => {
    checkSubscriptionState();
    checkCreditLimit();
  }, [checkSubscriptionState, checkCreditLimit]);

  return null;
}
